import { Button, Modal } from '@components/common';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';

interface InterviewQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  questions: string[];
  onSave: (questions: string[]) => void;
  isSaving: boolean;
}

// Question with unique ID for stable keys
interface QuestionWithId {
  id: string;
  text: string;
}

export const InterviewQuestionsModal = ({
  isOpen,
  onClose,
  questions: initialQuestions,
  onSave,
  isSaving,
}: InterviewQuestionsModalProps) => {
  const { t } = useTranslation('common');
  const [questions, setQuestions] = useState<QuestionWithId[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState<string>('');

  // Update local state when initialQuestions prop changes
  useEffect(() => {
    // Convert string array to QuestionWithId array with stable IDs
    const questionsWithIds = initialQuestions.map((text, index) => ({
      id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
      text,
    }));
    setQuestions(questionsWithIds);
  }, [initialQuestions]);

  const handleDelete = useCallback((id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  }, []);

  const startEditing = useCallback((id: string, text: string) => {
    setEditingId(id);
    setEditText(text);
  }, []);

  const saveEdit = useCallback(() => {
    if (editingId !== null && editText.trim()) {
      setQuestions((prev) => prev.map((q) => (q.id === editingId ? { ...q, text: editText.trim() } : q)));
      setEditingId(null);
      setEditText('');
    }
  }, [editingId, editText]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText('');
  }, []);

  const handleSave = useCallback(() => {
    if (questions.length === 0) {
      return;
    }
    // Convert back to string array for the API
    onSave(questions.map((q) => q.text));
  }, [questions, onSave]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <Modal.Header onClose={onClose}>
        {t('popup.interview.questions.title')}
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('cv.detail.interview_questions.modal_description', 'Review and edit the generated interview questions. You can delete or modify them before saving.')}
          </p>

          {questions.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
              <p className="text-gray-500">
                {t('cv.detail.interview_questions.no_questions', 'No questions available. Please add at least one question.')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((question, index) => (
                <div
                  key={question.id}
                  className="rounded-lg border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md"
                >
                  {editingId === question.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        rows={3}
                      />
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          color="error"
                          onClick={cancelEdit}
                        >
                          {t('global.action.cancel')}
                        </Button>
                        <Button
                          size="sm"
                          color="secondary"
                          onClick={saveEdit}
                          disabled={!editText.trim()}
                        >
                          {t('global.action.save')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <span className="mr-2 font-semibold text-primary">
                          {index + 1}
                          .
                        </span>
                        <span className="text-gray-800">{question.text}</span>
                      </div>
                      <div className="ml-4 flex space-x-2">
                        <button
                          type="button"
                          onClick={() => startEditing(question.id, question.text)}
                          className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary"
                          title={t('common.edit', 'Edit')}
                        >
                          <PencilIcon className="size-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(question.id)}
                          className="rounded p-1 text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600"
                          title={t('common.delete', 'Delete')}
                        >
                          <TrashIcon className="size-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-center space-x-2 border-t border-gray-200 pt-4">
            <Button
              size="sm"
              color="error"
              onClick={onClose}
              disabled={isSaving}
            >
              {t('global.action.cancel')}
            </Button>
            <Button
              size="sm"
              color="secondary"
              onClick={handleSave}
              disabled={questions.length === 0 || isSaving}
              isLoading={isSaving}
            >
              {t('global.action.save')}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
